import { supabase } from '../lib/supabase';
import {
  TournamentV2,
  TournamentV2WithDetails,
  TournamentEntrant,
  TournamentV2Match,
  TournamentMatchAssignment,
  TournamentMatchResult,
  TournamentBracketGraph,
  ReadyMatch,
  CreateTournamentV2Params,
  TournamentTemplate,
  BracketType,
  MatchState,
} from '../types';

// ============================================================================
// TOURNAMENT V2 - CRUD OPERATIONS
// ============================================================================

/**
 * Get all tournaments for a club (V2)
 */
export const getTournamentsV2 = async (clubId: number): Promise<TournamentV2[]> => {
  const { data, error } = await supabase
    .from('tournaments')
    .select(`
      id,
      club_id,
      name,
      description,
      status,
      start_date,
      end_date,
      format,
      created_by,
      created_at,
      settings
    `)
    .eq('club_id', clubId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching tournaments v2:', error);
    throw error;
  }

  return data || [];
};

/**
 * Get single tournament with details
 */
export const getTournamentV2 = async (tournamentId: number): Promise<TournamentV2WithDetails> => {
  const { data, error } = await supabase
    .from('tournaments')
    .select(`
      id,
      club_id,
      name,
      description,
      status,
      start_date,
      end_date,
      format,
      created_by,
      created_at,
      settings,
      club:clubs(id, name, description, created_at, status, settings, created_by),
      created_by_player:players!tournaments_created_by_fkey(id, name, "preName", email)
    `)
    .eq('id', tournamentId)
    .single();

  if (error) {
    console.error('Error fetching tournament v2:', error);
    throw error;
  }

  // Transform array relations to single objects
  const transformed = {
    ...data,
    club: Array.isArray(data.club) ? data.club[0] : data.club,
    created_by_player: Array.isArray(data.created_by_player) ? data.created_by_player[0] : data.created_by_player,
  };

  return transformed as TournamentV2WithDetails;
};

/**
 * Create a new tournament
 */
export const createTournamentV2 = async (params: CreateTournamentV2Params): Promise<TournamentV2> => {
  const { data: authData } = await supabase.auth.getUser();
  if (!authData.user) throw new Error('Not authenticated');

  // Get current player
  const { data: player } = await supabase
    .from('players')
    .select('id')
    .eq('playerUUID', authData.user.id)
    .single();

  if (!player) throw new Error('Player not found');

  // Create tournament
  const { data, error } = await supabase
    .from('tournaments')
    .insert({
      club_id: params.club_id,
      name: params.name,
      description: params.description,
      format: params.format,
      start_date: params.start_date,
      status: 'upcoming',
      created_by: player.id,
      settings: {
        participant_count: params.participant_count,
        default_frames: params.default_frames || 3,
      },
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating tournament v2:', error);
    throw error;
  }

  return data;
};

/**
 * Generate bracket structure for a tournament
 */
export const generateBracket = async (
  tournamentId: number,
  format: 'single_elimination' | 'double_elimination',
  participantCount: 16 | 8 | 32
): Promise<{ success: boolean; error?: string }> => {
  let functionName = '';

  // Map format and participant count to function name
  if (format === 'single_elimination') {
    if (participantCount === 8) {
      functionName = 'create_8_single_elimination_bracket';
    } else if (participantCount === 16) {
      functionName = 'create_16_single_elimination_bracket';
    } else if (participantCount === 32) {
      functionName = 'create_32_single_elimination_bracket';
    }
  } else if (format === 'double_elimination') {
    if (participantCount === 8) {
      functionName = 'create_8_double_elimination_bracket';
    } else if (participantCount === 16) {
      functionName = 'create_16_double_elimination_bracket';
    } else if (participantCount === 32) {
      functionName = 'create_32_double_elimination_bracket';
    }
  }

  if (!functionName) {
    const errorMsg = `Unsupported format/participant count combination: ${format} with ${participantCount} participants. Currently only 16-player single elimination is fully implemented.`;
    console.error(errorMsg);
    return { 
      success: false, 
      error: errorMsg
    };
  }

  console.log(`Calling bracket generation function: ${functionName} for tournament ${tournamentId}`);

  const { data, error } = await supabase.rpc(functionName, {
    p_tournament_id: tournamentId,
  });

  if (error) {
    console.error(`Error calling ${functionName}:`, error);
    
    // Check if function doesn't exist
    if (error.code === '42883' || error.message?.includes('does not exist')) {
      const notImplementedMsg = `The SQL function '${functionName}' is not implemented yet. Currently only 16-player single elimination tournaments are fully supported.`;
      console.error(notImplementedMsg);
      return {
        success: false,
        error: notImplementedMsg
      };
    }
    
    throw error;
  }

  console.log(`Bracket generation result from ${functionName}:`, data);
  return data;
};

// ============================================================================
// ENTRANT OPERATIONS
// ============================================================================

/**
 * Get all entrants for a tournament
 */
export const getTournamentEntrants = async (tournamentId: number): Promise<TournamentEntrant[]> => {
  const { data, error } = await supabase
    .from('tournament_entrants')
    .select(`
      *,
      player:players(id, name, "preName", email)
    `)
    .eq('tournament_id', tournamentId)
    .order('seed', { ascending: true, nullsFirst: false });

  if (error) {
    console.error('Error fetching entrants:', error);
    throw error;
  }

  return data || [];
};

/**
 * Register player for tournament
 */
export const registerForTournament = async (
  tournamentId: number,
  playerId: number
): Promise<TournamentEntrant> => {
  const { data, error } = await supabase
    .from('tournament_entrants')
    .insert({
      tournament_id: tournamentId,
      player_id: playerId,
      status: 'registered',
    })
    .select()
    .single();

  if (error) {
    console.error('Error registering for tournament:', error);
    throw error;
  }

  return data;
};

/**
 * Withdraw from tournament
 */
export const withdrawFromTournament = async (entrantId: string): Promise<void> => {
  const { error } = await supabase
    .from('tournament_entrants')
    .update({ status: 'withdrawn' })
    .eq('id', entrantId);

  if (error) {
    console.error('Error withdrawing from tournament:', error);
    throw error;
  }
};

/**
 * Confirm entrant (admin action)
 */
export const confirmEntrant = async (entrantId: string): Promise<void> => {
  const { error } = await supabase
    .from('tournament_entrants')
    .update({ status: 'confirmed' })
    .eq('id', entrantId);

  if (error) {
    console.error('Error confirming entrant:', error);
    throw error;
  }
};

/**
 * Update entrant seed
 */
export const updateEntrantSeed = async (entrantId: string, seed: number): Promise<void> => {
  const { error } = await supabase
    .from('tournament_entrants')
    .update({ seed })
    .eq('id', entrantId);

  if (error) {
    console.error('Error updating seed:', error);
    throw error;
  }
};

/**
 * Reset tournament to upcoming state (removes all matches and resets status)
 */
export const resetTournament = async (tournamentId: number): Promise<{ success: boolean; message: string }> => {
  const { data: authData } = await supabase.auth.getUser();
  if (!authData.user) throw new Error('Not authenticated');

  // Check if user can manage this tournament
  const canManage = await canManageTournamentV2(tournamentId, authData.user.id);
  if (!canManage) throw new Error('Insufficient permissions to reset tournament');

  try {
    console.log('Resetting tournament:', tournamentId);

    // Use SQL function to reset tournament (bypasses RLS policies)
    const { data, error } = await supabase.rpc('reset_tournament_v2', {
      p_tournament_id: tournamentId
    });

    if (error) {
      console.error('Error resetting tournament:', error);
      throw error;
    }

    console.log('Tournament reset result:', data);

    return {
      success: data.success,
      message: data.message || 'Tournament successfully reset to upcoming state'
    };
  } catch (error) {
    console.error('Error resetting tournament:', error);
    throw error;
  }
};

/**
 * Test function to manually trigger bracket generation
 */
export const testBracketGeneration = async (tournamentId: number): Promise<void> => {
  console.log('=== TESTING BRACKET GENERATION ===');
  
  try {
    // Get tournament details
    const tournament = await getTournamentV2(tournamentId);
    console.log('Tournament details:', tournament);
    
    // Try to generate bracket
    const result = await generateBracket(
      tournamentId,
      tournament.format as 'single_elimination' | 'double_elimination',
      tournament.settings?.participant_count as 16 | 8 | 32
    );
    
    console.log('Bracket generation result:', result);
    
    // Check what was created
    await debugTournamentState(tournamentId);
    
  } catch (error) {
    console.error('Error in test bracket generation:', error);
  }
  
  console.log('=== END TEST ===');
};

/**
 * Debug function to check tournament state
 */
export const debugTournamentState = async (tournamentId: number): Promise<void> => {
  console.log('=== DEBUGGING TOURNAMENT STATE ===');
  
  // Check tournament details
  const tournament = await getTournamentV2(tournamentId);
  console.log('Tournament:', tournament);
  
  // Check entrants
  const entrants = await getTournamentEntrants(tournamentId);
  console.log('Entrants:', entrants);
  
  // Check matches
  const { data: matches, error } = await supabase
    .from('tournament_v2_matches')
    .select('*')
    .eq('tournament_id', tournamentId);
  
  console.log('Matches:', matches, 'Error:', error);
  console.log('Match states:', matches?.map(m => ({ id: m.id, state: m.state, round: m.round_number, match: m.match_number })));
  
  // Check match slots
  if (matches && matches.length > 0) {
    for (const match of matches) {
      const { data: slots } = await supabase
        .from('tournament_match_slots')
        .select('*')
        .eq('match_id', match.id);
      console.log(`Slots for match ${match.id}:`, slots);
    }
  }
  
  console.log('=== END DEBUG ===');
};

/**
 * Force set Round 1 matches to ready (for debugging)
 */
export const forceReadyRound1Matches = async (tournamentId: number): Promise<void> => {
  console.log('=== FORCING ROUND 1 MATCHES TO READY ===');
  
  const { data: matches, error } = await supabase
    .from('tournament_v2_matches')
    .update({ state: 'ready' })
    .eq('tournament_id', tournamentId)
    .eq('round_number', 1)
    .select();

  if (error) {
    console.error('Error updating matches:', error);
    throw error;
  }

  console.log('Updated matches to ready:', matches);
  console.log('=== END FORCE READY ===');
};

/**
 * Start a tournament (create bracket and mark as active)
 */
export const startTournament = async (tournamentId: number): Promise<{ success: boolean; message: string }> => {
  const { data: authData } = await supabase.auth.getUser();
  if (!authData.user) throw new Error('Not authenticated');

  // Check if user can manage this tournament
  const canManage = await canManageTournamentV2(tournamentId, authData.user.id);
  if (!canManage) throw new Error('Insufficient permissions to start tournament');

  // Get tournament details
  const tournament = await getTournamentV2(tournamentId);
  console.log('Starting tournament:', tournament);
  console.log('Tournament format:', tournament.format);
  console.log('Tournament participant_count:', tournament.settings?.participant_count);
  
  // Get confirmed participants
  const entrants = await getTournamentEntrants(tournamentId);
  const confirmedEntrants = entrants.filter(e => e.status === 'confirmed');
  console.log('Confirmed entrants:', confirmedEntrants.length);
  
  // Validate minimum participants
  const minParticipants = tournament.settings?.participant_count || 16;
  if (confirmedEntrants.length < 4) {
    throw new Error(`Need at least 4 confirmed participants. Currently: ${confirmedEntrants.length}`);
  }

  // Auto-assign seeds if missing
  const unseededEntrants = confirmedEntrants.filter(e => !e.seed);
  if (unseededEntrants.length > 0) {
    console.log(`Auto-assigning seeds to ${unseededEntrants.length} unseeded participants`);
    
    // Assign consecutive seeds starting from 1
    for (let i = 0; i < unseededEntrants.length; i++) {
      await updateEntrantSeed(unseededEntrants[i].id, i + 1);
    }
    
    // Reload entrants to get updated seeds
    const updatedEntrants = await getTournamentEntrants(tournamentId);
    const updatedConfirmedEntrants = updatedEntrants.filter(e => e.status === 'confirmed');
    console.log('Updated entrants with seeds:', updatedConfirmedEntrants.map(e => ({ id: e.id, seed: e.seed })));
  }

  // Final validation: all participants must have seeds
  const finalEntrants = await getTournamentEntrants(tournamentId);
  const finalConfirmedEntrants = finalEntrants.filter(e => e.status === 'confirmed');
  const stillUnseeded = finalConfirmedEntrants.filter(e => !e.seed);
  
  if (stillUnseeded.length > 0) {
    throw new Error(`${stillUnseeded.length} participants still don't have seeds assigned`);
  }

  try {
    // Generate bracket structure
    const bracketResult = await generateBracket(
      tournamentId,
      tournament.format as 'single_elimination' | 'double_elimination',
      tournament.settings?.participant_count as 16 | 8 | 32
    );

    if (!bracketResult.success) {
      throw new Error(bracketResult.error || 'Failed to generate bracket');
    }

    console.log('Bracket generated successfully for tournament:', tournamentId);

    // Debug tournament state after bracket generation
    await debugTournamentState(tournamentId);

    // Update tournament status to active
    await updateTournamentV2(tournamentId, { status: 'active' });
    console.log('Tournament status updated to active');

    // Resolve initial match assignments (Round 1 matches)
    await resolveInitialMatches(tournamentId);
    console.log('Initial matches resolved');

    // Resolve ALL pending matches (cascade from walkovers)
    await resolveAllPendingMatches(tournamentId);
    console.log('All pending matches resolved');

    // Debug tournament state after resolving matches
    await debugTournamentState(tournamentId);

    return {
      success: true,
      message: `Tournament started successfully with ${confirmedEntrants.length} participants`
    };
  } catch (error) {
    console.error('Error starting tournament:', error);
    throw error;
  }
};

/**
 * Resolve initial matches (Round 1) to make them ready
 */
export const resolveInitialMatches = async (tournamentId: number): Promise<void> => {
  console.log('Resolving initial matches for tournament:', tournamentId);
  
  // Get all Round 1 matches
  const { data: matches, error } = await supabase
    .from('tournament_v2_matches')
    .select('id')
    .eq('tournament_id', tournamentId)
    .eq('round_number', 1)
    .eq('state', 'pending');

  if (error) {
    console.error('Error fetching initial matches:', error);
    throw error;
  }

  console.log(`Found ${matches?.length || 0} Round 1 matches to resolve`);

  if (!matches || matches.length === 0) {
    console.warn('No Round 1 matches found for tournament:', tournamentId);
    return;
  }

  // Resolve assignments for each match
  for (const match of matches) {
    try {
      console.log('Resolving match:', match.id);
      await resolveMatchAssignments(match.id);
      console.log('Successfully resolved match:', match.id);
    } catch (err) {
      console.error(`Error resolving match ${match.id}:`, err);
      // Continue with other matches even if one fails
    }
  }
};

/**
 * Resolve all pending matches (cascade from walkovers)
 */
export const resolveAllPendingMatches = async (tournamentId: number): Promise<void> => {
  console.log('Resolving all pending matches for tournament:', tournamentId);
  
  // Get ALL pending matches (any round)
  const { data: matches, error } = await supabase
    .from('tournament_v2_matches')
    .select('id, round_number')
    .eq('tournament_id', tournamentId)
    .eq('state', 'pending')
    .order('round_number', { ascending: true });

  if (error) {
    console.error('Error fetching pending matches:', error);
    throw error;
  }

  console.log(`Found ${matches?.length || 0} pending matches to resolve`);

  if (!matches || matches.length === 0) {
    return;
  }

  // Resolve assignments for each match
  for (const match of matches) {
    try {
      console.log(`Resolving Round ${match.round_number} match:`, match.id);
      await resolveMatchAssignments(match.id);
      console.log('Successfully resolved match:', match.id);
    } catch (err) {
      console.error(`Error resolving match ${match.id}:`, err);
      // Continue with other matches even if one fails
    }
  }
};

// ============================================================================
// MATCH OPERATIONS
// ============================================================================

/**
 * Get all tournament matches (for management view)
 */
export const getReadyMatches = async (tournamentId: number): Promise<ReadyMatch[]> => {
  // Get ALL matches regardless of state
  const { data: matches, error } = await supabase
    .from('tournament_v2_matches')
    .select('id, bracket, round_number, match_number, state, frames_to_play, game_id')
    .eq('tournament_id', tournamentId)
    .order('round_number', { ascending: true })
    .order('match_number', { ascending: true });

  if (error) {
    console.error('Error fetching ready matches:', error);
    throw error;
  }

  if (!matches || matches.length === 0) {
    return [];
  }

  // Get player names and scores for each match
  const matchesWithPlayers = await Promise.all(
    matches.map(async (match) => {
      const assignments = await getMatchAssignments(match.id);
      
      const player1 = assignments.find(a => a.slot_number === 1);
      const player2 = assignments.find(a => a.slot_number === 2);
      
      // Get match result if completed
      let result = null;
      if (match.state === 'completed') {
        result = await getMatchResult(match.id);
      }
      
      return {
        match_id: match.id,
        bracket: match.bracket,
        round_number: match.round_number,
        match_number: match.match_number,
        state: match.state,
        frames_to_play: match.frames_to_play,
        game_id: match.game_id,
        player_1_name: player1?.entrant?.player 
          ? `${player1.entrant.player.preName || ''} ${player1.entrant.player.name}`.trim()
          : 'TBD',
        player_2_name: player2?.entrant?.player
          ? `${player2.entrant.player.preName || ''} ${player2.entrant.player.name}`.trim()
          : 'TBD',
        slot_1_score: result?.slot_1_score ?? null,
        slot_2_score: result?.slot_2_score ?? null,
        winner_slot: result?.winner_slot ?? null,
      };
    })
  );

  return matchesWithPlayers;
};

/**
 * Get all matches for a tournament
 */
export const getTournamentMatches = async (tournamentId: number): Promise<TournamentV2Match[]> => {
  const { data, error } = await supabase
    .from('tournament_v2_matches')
    .select('*')
    .eq('tournament_id', tournamentId)
    .order('bracket')
    .order('round_number')
    .order('match_number');

  if (error) {
    console.error('Error fetching matches:', error);
    throw error;
  }

  return data || [];
};

/**
 * Get match assignments
 */
export const getMatchAssignments = async (matchId: string): Promise<TournamentMatchAssignment[]> => {
  const { data, error } = await supabase
    .from('tournament_match_assignments')
    .select(`
      *,
      entrant:tournament_entrants(
        *,
        player:players(id, name, "preName")
      )
    `)
    .eq('match_id', matchId)
    .order('slot_number');

  if (error) {
    console.error('Error fetching match assignments:', error);
    throw error;
  }

  return data || [];
};

/**
 * Start a match
 */
export const startMatchV2 = async (matchId: string): Promise<void> => {
  const { error } = await supabase.rpc('start_tournament_match', {
    p_match_id: matchId,
  });

  if (error) {
    console.error('Error starting match:', error);
    throw error;
  }
};

/**
 * Start a tournament match and create a game for it
 */
export const startTournamentMatchWithGame = async (matchId: string): Promise<{ gameId: number; matchId: string }> => {
  // Get match details with assignments
  const assignments = await getMatchAssignments(matchId);
  
  if (assignments.length !== 2) {
    throw new Error('Match must have exactly 2 players assigned');
  }

  // Get player IDs from assignments
  const playerAId = assignments.find(a => a.slot_number === 1)?.entrant?.player_id;
  const playerBId = assignments.find(a => a.slot_number === 2)?.entrant?.player_id;

  if (!playerAId || !playerBId) {
    throw new Error('Both players must be assigned to the match');
  }

  // Get match details to determine frames
  const { data: match, error: matchError } = await supabase
    .from('tournament_v2_matches')
    .select('frames_to_play')
    .eq('id', matchId)
    .single();

  if (matchError) {
    console.error('Error fetching match details:', matchError);
    throw matchError;
  }

  // Create the actual snooker game using SECURITY DEFINER function
  // This bypasses RLS so organizers can create games for any players
  const { data: gameIdData, error: gameError } = await supabase
    .rpc('create_game_for_tournament_match', {
      p_player_a_id: playerAId,
      p_player_b_id: playerBId,
      p_frames_to_play: match.frames_to_play,
      p_match_id: matchId
    });

  if (gameError) {
    console.error('Error creating game for match:', gameError);
    throw gameError;
  }

  const gameId = gameIdData;

  // Match state and game_id are already updated by the SECURITY DEFINER function
  // No need for additional updates or startMatchV2 call

  return {
    gameId: gameId,
    matchId: matchId
  };
};

/**
 * Reset a match from in_progress back to ready
 * This allows restarting a match if needed
 * Also deletes the associated game, frames, and breaks
 */
export const resetMatchToReady = async (matchId: string): Promise<void> => {
  // First, get the match to find the game_id
  const { data: match, error: fetchError } = await supabase
    .from('tournament_v2_matches')
    .select('game_id')
    .eq('id', matchId)
    .single();

  if (fetchError) {
    console.error('Error fetching match:', fetchError);
    throw fetchError;
  }

  // If there's a game_id, delete the associated game (cascade will delete frames & breaks)
  if (match?.game_id) {
    console.log(`🗑️ Deleting game ${match.game_id} and its frames/breaks...`);
    
    // First, get all frame IDs for this game
    const { data: frames } = await supabase
      .from('frames')
      .select('id')
      .eq('gameId', match.game_id);
    
    if (frames && frames.length > 0) {
      const frameIds = frames.map(f => f.id);
      
      // Delete breaks that reference these frames
      const { error: breaksError } = await supabase
        .from('breaks')
        .delete()
        .in('frameId', frameIds);
      
      if (breaksError) {
        console.error('Error deleting breaks:', breaksError);
        // Continue anyway, might not have breaks
      }
    }
    
    // Delete frames (they reference the game)
    const { error: framesError } = await supabase
      .from('frames')
      .delete()
      .eq('gameId', match.game_id);
    
    if (framesError) {
      console.error('Error deleting frames:', framesError);
      // Continue anyway
    }
    
    // Delete the game itself
    const { error: gameError } = await supabase
      .from('games')
      .delete()
      .eq('id', match.game_id);
    
    if (gameError) {
      console.error('Error deleting game:', gameError);
      throw gameError;
    }
    
    console.log(`✅ Game ${match.game_id} deleted successfully`);
  }

  // Reset the match state
  const { error } = await supabase
    .from('tournament_v2_matches')
    .update({
      state: 'ready',
      game_id: null,
    })
    .eq('id', matchId);

  if (error) {
    console.error('Error resetting match to ready:', error);
    throw error;
  }
};

/**
 * Get tournament match by game ID
 */
export const getMatchByGameId = async (gameId: number): Promise<TournamentV2Match | null> => {
  const { data, error } = await supabase
    .from('tournament_v2_matches')
    .select('*')
    .eq('game_id', gameId)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching match by game ID:', error);
    throw error;
  }

  return data || null;
};

/**
 * Record match result and advance tournament
 */
export const recordMatchResult = async (
  matchId: string,
  winnerSlot: 1 | 2,
  slot1Score: number,
  slot2Score: number
): Promise<{ success: boolean; tournament_complete: boolean }> => {
  const { data: authData } = await supabase.auth.getUser();
  
  // Try to get player ID, but allow null for table devices (anonymous users)
  let recordedBy = null;
  if (authData.user) {
    try {
      const { data: playerData } = await supabase
        .from('players')
        .select('id')
        .eq('playerUUID', authData.user.id)
        .single();
      recordedBy = playerData?.id || null;
    } catch (err) {
      console.log('No player found for user (likely table device), continuing with null recordedBy');
    }
  }

  const { data, error } = await supabase.rpc('record_match_result', {
    p_match_id: matchId,
    p_winner_slot: winnerSlot,
    p_slot_1_score: slot1Score,
    p_slot_2_score: slot2Score,
    p_recorded_by: recordedBy,
  });

  if (error) {
    console.error('Error recording match result:', error);
    throw error;
  }

  return data;
};

/**
 * Get match result
 */
export const getMatchResult = async (matchId: string): Promise<TournamentMatchResult | null> => {
  const { data, error } = await supabase
    .from('tournament_match_results')
    .select('*')
    .eq('match_id', matchId)
    .single();

  if (error && error.code !== 'PGRST116') { // Ignore "not found" error
    console.error('Error fetching match result:', error);
    throw error;
  }

  return data;
};

// ============================================================================
// BRACKET OPERATIONS
// ============================================================================

/**
 * Get complete bracket graph for rendering
 */
export const getTournamentBracketGraph = async (tournamentId: number): Promise<TournamentBracketGraph> => {
  const { data, error } = await supabase.rpc('get_tournament_bracket_graph', {
    p_tournament_id: tournamentId,
  });

  if (error) {
    console.error('Error fetching bracket graph:', error);
    throw error;
  }

  return data;
};

/**
 * Resolve match assignments (assign players based on slots)
 */
export const resolveMatchAssignments = async (matchId: string): Promise<void> => {
  // Call the SQL function to actually resolve player assignments
  const { data, error } = await supabase.rpc('resolve_match_assignments', {
    p_match_id: matchId
  });

  if (error) {
    console.error('Error resolving match assignments:', error);
    throw error;
  }

  console.log('Match assignments resolved:', data);
};

// ============================================================================
// TEMPLATE OPERATIONS
// ============================================================================

/**
 * Get tournament templates
 */
export const getTournamentTemplates = async (): Promise<TournamentTemplate[]> => {
  const { data, error } = await supabase
    .from('tournament_templates')
    .select('*')
    .or('is_public.eq.true')
    .order('participant_count')
    .order('name');

  if (error) {
    console.error('Error fetching templates:', error);
    throw error;
  }

  return data || [];
};

/**
 * Create tournament from template
 */
export const createFromTemplate = async (
  templateId: string,
  tournamentData: Omit<CreateTournamentV2Params, 'participant_count'>
): Promise<TournamentV2> => {
  // Get template
  const { data: template, error: templateError } = await supabase
    .from('tournament_templates')
    .select('*')
    .eq('id', templateId)
    .single();

  if (templateError) throw templateError;

  // Create tournament with template's participant count
  const tournament = await createTournamentV2({
    ...tournamentData,
    participant_count: template.participant_count as 16 | 8 | 32,
  });

  // Apply template structure (would need to parse template.structure JSONB)
  // For now, just generate standard bracket
  await generateBracket(
    tournament.id,
    tournamentData.format,
    template.participant_count as 16 | 8 | 32
  );

  return tournament;
};

// ================================================================
// BYE SYSTEM FUNCTIONS
// ================================================================

export const getTournamentByeInfo = async (tournamentId: number) => {
  try {
    const { data, error } = await supabase.rpc('get_tournament_bye_info', {
      p_tournament_id: tournamentId
    });
    
    if (error) {
      console.error('Error getting tournament bye info:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in getTournamentByeInfo:', error);
    throw error;
  }
};

export const distributeByesInTournament = async (tournamentId: number) => {
  try {
    const { data, error } = await supabase.rpc('distribute_byes_in_tournament', {
      p_tournament_id: tournamentId
    });
    
    if (error) {
      console.error('Error distributing byes:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in distributeByesInTournament:', error);
    throw error;
  }
};

export const smartSeedTournamentWithByes = async (tournamentId: number) => {
  try {
    const { data, error } = await supabase.rpc('smart_seed_tournament_with_byes', {
      p_tournament_id: tournamentId
    });
    
    if (error) {
      console.error('Error smart seeding tournament with byes:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in smartSeedTournamentWithByes:', error);
    throw error;
  }
};

export const resolveByeMatches = async (tournamentId: number) => {
  try {
    const { data, error } = await supabase.rpc('resolve_bye_matches', {
      p_tournament_id: tournamentId
    });
    
    if (error) {
      console.error('Error resolving bye matches:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in resolveByeMatches:', error);
    throw error;
  }
};

export const startTournamentWithByes = async (tournamentId: number) => {
  try {
    console.log('Starting tournament with bye handling:', tournamentId);
    
    const { data, error } = await supabase.rpc('start_tournament_with_byes', {
      p_tournament_id: tournamentId
    });
    
    if (error) {
      console.error('Error starting tournament with byes:', error);
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('Error in startTournamentWithByes:', error);
    throw error;
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get tournament status summary
 */
export const getTournamentSummary = async (tournamentId: number) => {
  const [tournament, entrants, readyMatches] = await Promise.all([
    getTournamentV2(tournamentId),
    getTournamentEntrants(tournamentId),
    getReadyMatches(tournamentId),
  ]);

  const confirmedEntrants = entrants.filter(e => e.status === 'confirmed');
  const registeredEntrants = entrants.filter(e => e.status === 'registered');

  return {
    tournament,
    total_entrants: entrants.length,
    confirmed_entrants: confirmedEntrants.length,
    registered_entrants: registeredEntrants.length,
    ready_matches: readyMatches.length,
    entrants,
  };
};

/**
 * Check if user can manage tournament
 */
export const canManageTournamentV2 = async (
  tournamentId: number,
  userId: string
): Promise<boolean> => {
  const { data: player } = await supabase
    .from('players')
    .select('id')
    .eq('playerUUID', userId)
    .single();

  if (!player) return false;

  const { data: tournament } = await supabase
    .from('tournaments')
    .select('club_id')
    .eq('id', tournamentId)
    .single();

  if (!tournament) return false;

  const { data: membership } = await supabase
    .from('club_members')
    .select('role')
    .eq('club_id', tournament.club_id)
    .eq('player_id', player.id)
    .eq('status', 'active')
    .single();

  return membership?.role && ['owner', 'admin', 'organizer'].includes(membership.role);
};

/**
 * Update tournament status
 */
export const updateTournamentStatus = async (
  tournamentId: number,
  status: 'upcoming' | 'active' | 'completed' | 'cancelled'
): Promise<void> => {
  const { error } = await supabase
    .from('tournaments')
    .update({ status })
    .eq('id', tournamentId);

  if (error) {
    console.error('Error updating tournament status:', error);
    throw error;
  }
};

/**
 * Update tournament details
 */
export const updateTournamentV2 = async (
  tournamentId: number,
  updates: {
    name?: string;
    description?: string;
    start_date?: string;
    end_date?: string;
    status?: 'upcoming' | 'active' | 'completed' | 'cancelled';
  }
): Promise<TournamentV2> => {
  // Check if user can manage this tournament
  const { data: authData } = await supabase.auth.getUser();
  if (!authData.user) throw new Error('Not authenticated');

  const canManage = await canManageTournamentV2(tournamentId, authData.user.id);
  if (!canManage) throw new Error('Insufficient permissions to update tournament');

  const { data, error } = await supabase
    .from('tournaments')
    .update(updates)
    .eq('id', tournamentId)
    .select()
    .single();

  if (error) {
    console.error('Error updating tournament:', error);
    throw error;
  }

  return data;
};

/**
 * Delete tournament and all associated data
 */
export const deleteTournamentV2 = async (tournamentId: number): Promise<void> => {
  // Check if user can manage this tournament
  const { data: authData } = await supabase.auth.getUser();
  if (!authData.user) throw new Error('Not authenticated');

  const canManage = await canManageTournamentV2(tournamentId, authData.user.id);
  if (!canManage) throw new Error('Insufficient permissions to delete tournament');

  // Since the database has CASCADE DELETE constraints, we can simply delete the tournament
  // and all related data will be automatically deleted
  const { error } = await supabase
    .from('tournaments')
    .delete()
    .eq('id', tournamentId);

  if (error) {
    console.error('Error deleting tournament:', error);
    throw error;
  }
};

